// Ведомость инженера — баллы, бейджи и выплаты портала 73. ТОЛЬКО ЧТЕНИЕ.
//
// ЧТО ЭТОТ ЭКРАН ОТВЕЧАЕТ. Не «как у нас с мотивацией», а три вопроса про деньги:
// сколько человеку начислено, сколько ему уже выплатили и сколько мы ему ещё должны.
// Курс 1 балл = 1 ₽ посчитан ядром по проведённым выплатам, а не вбит константой.
//
// ГЛАВНОЕ, ЧТО ОБЯЗАНО БЫТЬ ВИДНО, И ПОЧЕМУ ИМЕННО ЭТО:
//   • «начислено» в источнике живёт в ДВУХ видах — баланс (users.total_points) и журнал
//     (bonus_log). Портал пишет их двумя запросами без общей транзакции, и они разошлись.
//     Экран показывает ОБА числа и разницу. Показать одно значило бы решить за владельца,
//     какая половина данных врёт;
//   • умолчание «только не отключённые учётки» прячет пять учёток и почти сто тысяч
//     баллов. Спрятать и промолчать — это соврать тихо, поэтому число стоит на экране;
//   • раздел НИЧЕГО НЕ МЕНЯЕТ. Начислить, списать, подать и одобрить заявку — это запись
//     в портал 73, а его нам трогать нельзя. Кнопка ведёт в портал, и рядом сказано почему.
//
// ЧЕГО ЗДЕСЬ НЕТ. Списка «в какие дни человек заходил», платёжных реквизитов и победителя
// месяца отдельной таблицей — см. блок «Чего на этом экране нет» внизу: он собран из
// оговорок ядра, а не написан здесь руками, и потому не может отстать от данных.
import { useMemo, useState } from 'react'

import {
  useБейджи, useВедомость, useВыплаты, useЗаявки, useНачисления, useP73GRefs, useРейтинг,
  useСходимость,
} from '../api/p73g'
import type {
  БейджСтрока, ВедомостьИтоги, ВедомостьСтрока, ВыплатаСтрока, ЗаявкаСтрока, НачислениеСтрока,
  ПроверкаСтрока, РейтингСтрока, СправочникиP73G, Сходимость,
} from '../api/p73g'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtMoney, plural, pluralN } from '../lib/format'
import {
  ИМЯ_СУЩНОСТИ, ПОРТАЛ_73_БАЛАНС, ПОРТАЛ_73_БЕЙДЖИ, ПОРТАЛ_73_РЕЙТИНГ, РАЗМЕР_СТРАНИЦЫ,
  баллы, виновникРасхождения, классСтатуса, курсСловами, месяцСловами, однимЧеловеком,
  поСправочнику, проверка, расхождение, свежесть, текущийМесяц, число,
} from '../lib/p73g'

type Вкладка = 'ledger' | 'accruals' | 'badges' | 'rating' | 'payouts' | 'requests' | 'checks'

export default function EngineerLedger() {
  const [вкладка, setВкладка] = useState<Вкладка>('ledger')
  const refs = useP73GRefs()
  // Сходимость нужна не только своей вкладке: из неё берётся ПОИМЁННЫЙ виновник главного
  // расхождения. Ключ запроса один, поэтому переход на вкладку «Сходимость» второго
  // обращения к ядру не делает.
  const свод = useСходимость()

  const счёт = refs.data?.counts
  const св = свежесть(refs.data?.freshness)

  return (
    <>
      <div className="page-head">
        <h1>Ведомость инженера</h1>
        <span className="spacer" />
        <span className="hint">
          {св
            ? `обновлено ${св.age_min < 1 ? 'только что' : `${Math.round(св.age_min)} мин назад`}`
              + ` (позже всех — ${ИМЯ_СУЩНОСТИ[св.entity] ?? св.entity})`
            : 'свежесть неизвестна'}
        </span>
      </div>

      <Неизменяемо />

      <QueryState isLoading={refs.isLoading} error={refs.error}>
        {refs.data && <Плитки refs={refs.data} />}
        <Расхождение счёт={счёт} свод={свод.data} />
        {счёт && <Умолчание счёт={счёт} />}

        {/* Переключатель вкладок — обычный flex, а НЕ .filters: на телефоне правило
            «.filters > * { width: 100% }» ставит кнопки в столбец, и семь вкладок
            занимают весь первый экран до единственного, ради чего его открыли. */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {([
            ['ledger', `Ведомость${счёт ? ` · ${счёт.accounts_active}` : ''}`],
            ['accruals', `Начисления${счёт ? ` · ${счёт.accruals}` : ''}`],
            ['badges', `Бейджи${счёт ? ` · ${счёт.badges}` : ''}`],
            ['rating', 'Рейтинг'],
            ['payouts', `Выплаты${счёт ? ` · ${счёт.payouts}` : ''}`],
            ['requests', `Заявки${счёт ? ` · ${счёт.requests_pending} ждут` : ''}`],
            ['checks', `Сходимость${свод.data ? ` · ${свод.data.checks_with_findings}` : ''}`],
          ] as [Вкладка, string][]).map(([код, подпись]) => (
            <button key={код} onClick={() => setВкладка(код)}
                    aria-pressed={вкладка === код}
                    style={{ fontWeight: вкладка === код ? 600 : 400, flex: '0 1 auto' }}>
              {подпись}
            </button>
          ))}
        </div>

        {вкладка === 'ledger' && <Ведомость refs={refs.data} />}
        {вкладка === 'accruals' && <Начисления refs={refs.data} />}
        {вкладка === 'badges' && <Бейджи />}
        {вкладка === 'rating' && <Рейтинг refs={refs.data} />}
        {вкладка === 'payouts' && <Выплаты />}
        {вкладка === 'requests' && <Заявки refs={refs.data} />}
        {вкладка === 'checks' && <Сходимости запрос={свод} />}

        <Оговорки список={refs.data?.caveats} источник={refs.data?.source} />
      </QueryState>
    </>
  )
}

/**
 * Раздел ничего не меняет — и это сказано ДО данных, а не мелким шрифтом внизу.
 *
 * Причина не в осторожности вообще: начисление в портале сцеплено с верификациями, курсом
 * и согласованием заявки. Перенести половину этой сцепки значило бы получить в CRM баланс,
 * который расходится с порталом в меньшую сторону, — то есть недоплату инженерам.
 */
function Неизменяемо() {
  return (
    <div className="notice" style={{ marginBottom: 12 }}>
      <b>Здесь заработок людей поимённо.</b> Отсюда нельзя ни начислить, ни списать, ни подать
      заявку на выплату, ни одобрить её: каждое из этих действий — запись в портал 73, а CRM
      его только читает. Начисление там сцеплено с верификациями, курсом и согласованием;
      перенести половину сцепки значило бы показывать баланс меньше настоящего.{' '}
      <a href={ПОРТАЛ_73_БАЛАНС} target="_blank" rel="noreferrer">
        Открыть баланс в портале 73 ↗
      </a>
    </div>
  )
}

/** Пометка ядра «в ответе деньги нескольких людей». Показываем, а не прячем. */
function Закрыто({ почему }: { почему?: string }) {
  if (!почему) return null
  return (
    <div className="notice" style={{ marginBottom: 12, fontSize: 12 }}>
      Выборка по всем людям: {почему}. Раздел открыт одной учётке — не показывайте экран
      через плечо.
    </div>
  )
}

// ═══════════════════════════ шапка: числа раздела ═══════════════════════════

function Плитки({ refs }: { refs: СправочникиP73G }) {
  const с = refs.counts
  return (
    <div className="tiles" style={{ marginBottom: 12 }}>
      <div className="tile">
        <div className="t-label">начислено по балансам</div>
        <div className="t-sum">{число(с.total_points)}</div>
        <div className="t-label">по журналу: {число(с.accrued_sum)}</div>
      </div>
      <div className="tile">
        <div className="t-label">выплачено</div>
        <div className="t-sum">{число(с.paid_points)}</div>
        <div className="t-label">
          реестр: {fmtMoney(с.payouts_rub)} по {pluralN(с.payouts, 'выплате', 'выплатам', 'выплатам')}
        </div>
      </div>
      <div className="tile hot">
        <div className="t-label">должны инженерам</div>
        <div className="t-sum">{число(с.available_points)}</div>
        <div className="t-label">{курсСловами(с)}</div>
      </div>
      <div className="tile">
        <div className="t-label">кому начисляли</div>
        <div className="t-sum">{с.people_earning}</div>
        <div className="t-label">
          учёток в зеркале {с.accounts}, из них не отключены {с.accounts_active}
        </div>
      </div>
      <div className="tile">
        <div className="t-label">бейджи</div>
        <div className="t-sum">{с.badges}</div>
        <div className="t-label">
          выдано {с.badges_awarded}; ни разу не выдавались {с.badges_never}
        </div>
      </div>
      <div className={с.requests_pending > 0 ? 'tile hot' : 'tile'}>
        <div className="t-label">заявки ждут рассмотрения</div>
        <div className="t-sum">{с.requests_pending}</div>
        <div className="t-label">на {fmtMoney(с.requests_pending_rub)}</div>
      </div>
    </div>
  )
}

/**
 * Баланс против журнала — главное, что этот экран обязан сказать вслух.
 *
 * Оба числа рядом и разница между ними. Виновник берётся из проверки balance_vs_log ядра
 * (его delta посчитана там же), а не вычисляется здесь: два расчёта одного числа однажды
 * разойдутся, и разойдутся молча.
 */
function Расхождение({ счёт, свод }: {
  счёт: СправочникиP73G['counts'] | undefined
  свод: Сходимость | undefined
}) {
  const р = расхождение(счёт)
  if (!р) return null
  const п = проверка(свод, 'balance_vs_log')
  const виновник = виновникРасхождения(свод)
  const однимИз = однимЧеловеком(р, виновник, п)
  const сходятся = счёт && п ? счёт.accounts - п.rows : null

  if (р.сходится) {
    return (
      <div className="card hint" style={{ marginBottom: 12 }}>
        <b>Баланс и журнал сходятся:</b> {баллы(р.балансы)} и там, и там. Портал пишет их
        двумя запросами без общей транзакции, поэтому это не данность, а сегодняшнее
        состояние — проверка живёт во вкладке «Сходимость».
      </div>
    )
  }

  return (
    <div className="notice" style={{ marginBottom: 12 }}>
      <b>Баланс и журнал разошлись на {баллы(Math.abs(р.разница))}.</b>{' '}
      Начислено по балансам <b>{число(р.балансы)}</b>, по журналу начислений{' '}
      <b>{число(р.журнал)}</b>. Правильного из этих двух чисел нет: колонку баланса портал
      накапливает отдельными UPDATE, а строку журнала пишет отдельным INSERT, общей
      транзакции между ними не существует — сбой посередине оставляет их разными навсегда.
      {виновник && (
        <div style={{ marginTop: 6 }}>
          Расхождение поимённое:{' '}
          <b>{виновник.full_name}</b> (учётка #{виновник.d1_id}) — баланс{' '}
          {число(виновник.total_points)} при {число(виновник.log_points)} по журналу,
          разница {число(виновник.delta)}.
          {однимИз && сходятся !== null && (
            <> Остальные {pluralN(сходятся, 'учётка', 'учётки', 'учёток')} из {счёт?.accounts}{' '}
              сходятся в копейку — то есть это не общая поломка синка, а одна строка.</>
          )}
        </div>
      )}
      <div className="hint" style={{ marginTop: 6 }}>
        Починить это можно только в портале 73 и только человеку: CRM в источник не пишет.
      </div>
    </div>
  )
}

/** Что прячет умолчание «только не отключённые». Числом, а не намёком. */
function Умолчание({ счёт }: { счёт: СправочникиP73G['counts'] }) {
  if (!счёт.hidden_accounts) return null
  return (
    <div className="card hint" style={{ marginBottom: 12 }}>
      <b>Ведомость по умолчанию показывает не всех.</b> Скрыто{' '}
      {pluralN(счёт.hidden_accounts, 'отключённая учётка', 'отключённые учётки',
               'отключённых учёток')}{' '}
      на {баллы(счёт.hidden_points)} — среди них «Демо-режим · просмотр дизайна», у которой
      баллов больше, чем у любого живого инженера. Показать всех: фильтр «Кого показывать» →
      «все учётки, включая демо и отключённые».
    </div>
  )
}

/** Оговорки ядра — с уже подставленными сегодняшними числами. */
function Оговорки({ список, источник }: { список?: string[]; источник?: string }) {
  if (!список?.length) return null
  return (
    <div className="card hint" style={{ marginTop: 12 }}>
      <b>Чего на этом экране нет и почему</b>
      <ul className="plain-list" style={{ marginTop: 6 }}>
        {список.map((т) => <li key={т}>{т}</li>)}
      </ul>
      {источник && <div style={{ marginTop: 6 }}>Источник: {источник}</div>}
    </div>
  )
}

// ═══════════════════════════ страницы ═══════════════════════════

function Страницы({ всего, start, setStart }: {
  всего: number; start: number; setStart: (v: number) => void
}) {
  if (всего <= РАЗМЕР_СТРАНИЦЫ) return null
  return (
    <div className="pager">
      <button disabled={start === 0} onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>
        назад
      </button>
      <span className="muted">
        {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, всего)} из {всего}
      </span>
      <button disabled={start + РАЗМЕР_СТРАНИЦЫ >= всего}
              onClick={() => setStart(start + РАЗМЕР_СТРАНИЦЫ)}>
        дальше
      </button>
    </div>
  )
}

// ═══════════════════════════ вкладка «Ведомость» ═══════════════════════════

function Ведомость({ refs }: { refs?: СправочникиP73G }) {
  const [кого, setКого] = useState('active')
  const [человек, setЧеловек] = useState('')
  const [порядок, setПорядок] = useState('debt')
  const [должны, setДолжны] = useState(false)
  const [поиск, setПоиск] = useState('')
  const [start, setStart] = useState(0)

  const фильтры = useMemo(() => ({
    who: кого,
    sort: порядок,
    owed: должны || undefined,
    q: поиск || undefined,
    engineer: человек ? Number(человек) : undefined,
    all: !человек,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [кого, порядок, должны, поиск, человек, start])

  const запрос = useВедомость(фильтры)
  const данные = запрос.data
  const итоги = данные?.totals
  const скрыто = данные?.hidden
  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  return (
    <>
      <Закрыто почему={данные?.restricted_why} />

      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="p73g-ledger">
          <div className="f">
            <label htmlFor="elw">Кого показывать</label>
            <select id="elw" value={кого} onChange={(e) => сСброса(setКого)(e.target.value)}>
              {Object.entries(refs?.who_names ?? { active: 'не отключённые учётки' })
                .map(([к, имя]) => <option key={к} value={к}>{имя}</option>)}
            </select>
          </div>
          <div className="f" style={{ minWidth: 200 }}>
            <label htmlFor="elp">Человек</label>
            <select id="elp" value={человек}
                    onChange={(e) => сСброса(setЧеловек)(e.target.value)}>
              <option value="">все (видны деньги всех)</option>
              {(refs?.people ?? []).map((ч) => (
                <option key={ч.id} value={String(ч.id)}>{ч.name}</option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="els">Порядок</label>
            <select id="els" value={порядок}
                    onChange={(e) => сСброса(setПорядок)(e.target.value)}>
              <option value="debt">сначала кому больше должны</option>
              <option value="total">сначала кому больше начислено</option>
              <option value="paid">сначала кому больше выплачено</option>
              <option value="name">по фамилии</option>
              <option value="recent">по последнему заходу</option>
            </select>
          </div>
          <div className="f">
            <label htmlFor="elq">Поиск по фамилии</label>
            <input id="elq" value={поиск} onChange={(e) => сСброса(setПоиск)(e.target.value)}
                   placeholder="часть фамилии" />
          </div>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 18 }}>
            <input type="checkbox" checked={должны}
                   onChange={(e) => сСброса(setДолжны)(e.target.checked)} />
            только те, кому должны
          </label>
        </FilterBar>
        {скрыто && скрыто.people > 0 && (
          <div className="hint" style={{ marginTop: 8 }}>
            Этот фильтр прячет {pluralN(скрыто.people, 'учётку', 'учётки', 'учёток')} на{' '}
            {баллы(скрыто.total_points)} — из них не выплачено {баллы(скрыто.available_points)}.
          </div>
        )}
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <ТаблицаВедомости строки={данные?.rows ?? []} итоги={итоги} refs={refs} />
        <Страницы всего={данные?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </>
  )
}

function ТаблицаВедомости({ строки, итоги, refs }: {
  строки: ВедомостьСтрока[]
  итоги?: ВедомостьИтоги
  refs?: СправочникиP73G
}) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">Под этот фильтр не попала ни одна учётка</div>
        <div className="empty-hint">
          Ведомость по умолчанию показывает только не отключённые учётки — попробуйте
          «все учётки, включая демо и отключённые».
        </div>
      </div>
    )
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Человек</th>
            <th className="num">Начислено</th>
            <th className="num">Выплачено</th>
            <th className="num">Должны</th>
            <th className="num">По журналу</th>
            <th>Бейджи и серия</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((r) => (
            <tr key={r.d1_id}>
              <td data-label="Человек">
                <div>{r.full_name}</div>
                <div className="hint">
                  {поСправочнику(r.level, refs?.level_names, 'уровень не указан')}
                  {r.is_active === false ? ' · учётка отключена' : ''}
                  {r.pending_requests > 0
                    ? ` · ${pluralN(r.pending_requests, 'заявка ждёт', 'заявки ждут',
                                    'заявок ждут')} рассмотрения`
                    : ''}
                </div>
              </td>
              <td className="num" data-label="Начислено">{число(r.total_points)}</td>
              <td className="num" data-label="Выплачено">
                {число(r.paid_points)}
                <div className="hint">{fmtMoney(r.paid_rub)} по реестру</div>
              </td>
              <td className="num" data-label="Должны">{число(r.available_points)}</td>
              <td className="num" data-label="По журналу">
                {число(r.accrued_sum)}
                {r.total_minus_log !== 0 && (
                  <div className="error" style={{ fontSize: 12 }}>
                    баланс больше журнала на {число(r.total_minus_log)}
                  </div>
                )}
                <div className="hint">
                  {pluralN(r.accruals, 'начисление', 'начисления', 'начислений')}
                  {r.last_accrual_at ? `, последнее ${fmtDate(r.last_accrual_at)}` : ''}
                </div>
              </td>
              <td data-label="Бейджи и серия">
                {r.badges}
                <div className="hint">
                  серия сейчас {r.current_streak ?? 0}, лучшая {r.longest_streak ?? 0}
                </div>
              </td>
            </tr>
          ))}
          {итоги && (
            <tr>
              <td data-label="Итого">
                <b>Итого по всей выборке</b>
                <div className="hint">
                  {pluralN(итоги.people, 'учётка', 'учётки', 'учёток')}, из них{' '}
                  {итоги.people_owed} с долгом
                  {итоги.people_negative > 0
                    ? ` и ${итоги.people_negative} с отрицательным остатком`
                    : ''}
                </div>
              </td>
              <td className="num" data-label="Начислено"><b>{число(итоги.total_points)}</b></td>
              <td className="num" data-label="Выплачено"><b>{число(итоги.paid_points)}</b></td>
              <td className="num" data-label="Должны"><b>{число(итоги.available_points)}</b></td>
              <td className="num" data-label="По журналу" />
              <td data-label="Бейджи и серия" />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

// ═══════════════════════════ вкладка «Начисления» ═══════════════════════════

function Начисления({ refs }: { refs?: СправочникиP73G }) {
  const [вид, setВид] = useState('')
  const [период, setПериод] = useState('')
  const [человек, setЧеловек] = useState('')
  const [поиск, setПоиск] = useState('')
  const [start, setStart] = useState(0)

  const фильтры = useMemo(() => ({
    kind: вид || undefined,
    period: период || undefined,
    q: поиск || undefined,
    engineer: человек ? Number(человек) : undefined,
    all: !человек,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [вид, период, поиск, человек, start])

  const запрос = useНачисления(фильтры)
  const данные = запрос.data
  const итоги = данные?.totals
  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  return (
    <>
      <Закрыто почему={данные?.restricted_why} />

      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="p73g-accruals">
          <div className="f">
            <label htmlFor="eak">Вид начисления</label>
            <select id="eak" value={вид} onChange={(e) => сСброса(setВид)(e.target.value)}>
              <option value="">любой</option>
              {(refs?.kinds ?? []).map((к) => (
                <option key={к.kind} value={к.kind}>
                  {поСправочнику(к.kind, refs?.kind_names)} ({к.accruals})
                </option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="eap">Месяц</label>
            <select id="eap" value={период}
                    onChange={(e) => сСброса(setПериод)(e.target.value)}>
              <option value="">все, что есть</option>
              {(refs?.periods ?? []).map((п) => (
                <option key={п.period} value={п.period}>
                  {месяцСловами(п.period)} ({п.accruals})
                </option>
              ))}
            </select>
          </div>
          <div className="f" style={{ minWidth: 200 }}>
            <label htmlFor="eae">Человек</label>
            <select id="eae" value={человек}
                    onChange={(e) => сСброса(setЧеловек)(e.target.value)}>
              <option value="">все (видны деньги всех)</option>
              {(refs?.people ?? []).map((ч) => (
                <option key={ч.id} value={String(ч.id)}>{ч.name}</option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="eaq">Поиск по причине</label>
            <input id="eaq" value={поиск} onChange={(e) => сСброса(setПоиск)(e.target.value)}
                   placeholder="часть текста причины" />
          </div>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          Вид начисления вычислен ядром по началу текста причины: кода вида в источнике нет
          вовсе. Сумма от разбора не зависит
          {итоги ? `; не разобрано строк: ${итоги.unparsed_kind}` : ''}.
        </div>
      </div>

      {итоги && (
        <div className="tiles" style={{ marginBottom: 12 }}>
          <div className="tile">
            <div className="t-label">начислений в выборке</div>
            <div className="t-sum">{итоги.accruals}</div>
            <div className="t-label">людей: {итоги.people}</div>
          </div>
          <div className="tile">
            <div className="t-label">баллов по журналу</div>
            <div className="t-sum">{число(итоги.points)}</div>
            <div className="t-label">итог по всей выборке, а не по странице</div>
          </div>
          <div className="tile">
            <div className="t-label">глубина истории</div>
            <div className="t-sum">{fmtDate(итоги.first_at)}</div>
            <div className="t-label">последнее: {fmtDate(итоги.last_at)}</div>
          </div>
          <div className={итоги.zero_points > 0 ? 'tile hot' : 'tile'}>
            <div className="t-label">начисления на ноль баллов</div>
            <div className="t-sum">{итоги.zero_points}</div>
            <div className="t-label">отрицательных: {итоги.negative}</div>
          </div>
        </div>
      )}

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <ТаблицаНачислений строки={данные?.rows ?? []} refs={refs} />
        <Страницы всего={данные?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </>
  )
}

function ТаблицаНачислений({ строки, refs }: {
  строки: НачислениеСтрока[]; refs?: СправочникиP73G
}) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">Под этот фильтр начислений нет</div>
        <div className="empty-hint">
          История в источнике начинается 28.05.2026 — за более ранние месяцы строк нет
          ни у кого, и это не значит, что тогда не начисляли.
        </div>
      </div>
    )
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Когда</th>
            <th>Кому</th>
            <th>За что</th>
            <th>Вид</th>
            <th className="num">Баллы</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((r) => (
            <tr key={r.d1_id}>
              <td data-label="Когда" className="nowrap">
                {fmtDate(r.created_at, true)}
              </td>
              <td data-label="Кому">
                <div>{r.full_name}</div>
                {r.is_active === false && <div className="hint">учётка отключена</div>}
              </td>
              <td data-label="За что">
                {r.reason || <span className="muted">причина не записана</span>}
                {r.verification_d1_id && (
                  <div className="hint">верификация #{r.verification_d1_id}</div>
                )}
              </td>
              <td data-label="Вид">
                <span className="badge">{поСправочнику(r.kind, refs?.kind_names)}</span>
              </td>
              <td className="num" data-label="Баллы">{число(r.points)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ═══════════════════════════ вкладка «Бейджи» ═══════════════════════════
//
// Единственный список раздела без чьих-либо денег: правило премирования, а не доход.
// Поэтому он не требует «назовите человека» и не помечен restricted.

function Бейджи() {
  const [вид, setВид] = useState('')
  const [порядок, setПорядок] = useState('awarded')
  const [поиск, setПоиск] = useState('')
  const [start, setStart] = useState(0)

  const фильтры = useMemo(() => ({
    kind: вид || undefined,
    sort: порядок,
    q: поиск || undefined,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [вид, порядок, поиск, start])

  const запрос = useБейджи(фильтры)
  const данные = запрос.data
  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="p73g-badges">
          <div className="f">
            <label htmlFor="ebk">Какие показать</label>
            <select id="ebk" value={вид} onChange={(e) => сСброса(setВид)(e.target.value)}>
              <option value="">все</option>
              <option value="never">ни разу не выдавались</option>
              <option value="awarded">выданные хотя бы раз</option>
              <option value="free">без ставки (0 баллов)</option>
            </select>
          </div>
          <div className="f">
            <label htmlFor="ebs">Порядок</label>
            <select id="ebs" value={порядок}
                    onChange={(e) => сСброса(setПорядок)(e.target.value)}>
              <option value="awarded">по числу выдач</option>
              <option value="cost">по тому, во что обошёлся</option>
              <option value="rate">по ставке</option>
              <option value="sort">как в портале</option>
            </select>
          </div>
          <div className="f">
            <label htmlFor="ebq">Поиск</label>
            <input id="ebq" value={поиск} onChange={(e) => сСброса(setПоиск)(e.target.value)}
                   placeholder="код, название или описание" />
          </div>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          Найдено: {данные?.total ?? 0}. «Во что обошёлся» — сегодняшняя ставка, умноженная
          на число выдач; если ставку когда-то меняли, начисленное по журналу будет другим.{' '}
          <a href={ПОРТАЛ_73_БЕЙДЖИ} target="_blank" rel="noreferrer">
            Каталог в портале 73 ↗
          </a>
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <ТаблицаБейджей строки={данные?.rows ?? []} вид={вид} />
        <Страницы всего={данные?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </>
  )
}

function ТаблицаБейджей({ строки, вид }: { строки: БейджСтрока[]; вид: string }) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">
          {вид === 'never' ? 'Невыданных бейджей не осталось' : 'Под этот фильтр бейджей нет'}
        </div>
        <div className="empty-hint">
          Каталог бейджей ядро перечитывает раз в три часа — свежезаведённый может появиться
          здесь не сразу.
        </div>
      </div>
    )
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Бейдж</th>
            <th className="num">Ставка</th>
            <th className="num">Выдан раз</th>
            <th className="num">Людям</th>
            <th className="num">Во что обошёлся</th>
            <th>Последняя выдача</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((b) => (
            <tr key={b.d1_id}>
              <td data-label="Бейдж">
                <div>{b.icon ? `${b.icon} ` : ''}{b.name || b.code || `#${b.d1_id}`}</div>
                <div className="hint">{b.description || b.code || '—'}</div>
              </td>
              <td className="num" data-label="Ставка">{число(b.bonus_points ?? 0)}</td>
              <td className="num" data-label="Выдан раз">
                {b.awarded}
                {b.awarded === 0 && (
                  <div className="hint">правило есть, повода не случилось</div>
                )}
              </td>
              <td className="num" data-label="Людям">{b.people}</td>
              <td className="num" data-label="Во что обошёлся">{число(b.cost_by_rate)}</td>
              <td data-label="Последняя выдача" className="nowrap">
                {b.last_awarded_at ? fmtDate(b.last_awarded_at) : <span className="muted">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ═══════════════════════════ вкладка «Рейтинг» ═══════════════════════════

function Рейтинг({ refs }: { refs?: СправочникиP73G }) {
  const [период, setПериод] = useState(текущийМесяц())
  const [деньги, setДеньги] = useState(false)
  const [start, setStart] = useState(0)

  const фильтры = useMemo(() => ({
    period: период || undefined,
    money: деньги || undefined,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [период, деньги, start])

  const запрос = useРейтинг(фильтры)
  const данные = запрос.data

  return (
    <>
      <Закрыто почему={данные?.restricted_why} />

      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="p73g-rating">
          <div className="f">
            <label htmlFor="erp">Месяц</label>
            <select id="erp" value={период}
                    onChange={(e) => { setStart(0); setПериод(e.target.value) }}>
              {(refs?.periods ?? []).map((п) => (
                <option key={п.period} value={п.period}>
                  {месяцСловами(п.period)} ({п.accruals})
                </option>
              ))}
              {!(refs?.periods ?? []).some((п) => п.period === период) && (
                <option value={период}>{месяцСловами(период)}</option>
              )}
            </select>
          </div>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 18 }}>
            <input type="checkbox" checked={деньги}
                   onChange={(e) => setДеньги(e.target.checked)} />
            показать баллы (это заработок людей)
          </label>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          Без галочки рейтинг отдаёт места, число верификаций, бейджей и серию — и ни одной
          суммы. Это разные права: места видно по работе, баллы — это деньги.{' '}
          <a href={ПОРТАЛ_73_РЕЙТИНГ} target="_blank" rel="noreferrer">
            Рейтинг в портале 73 ↗
          </a>
        </div>
      </div>

      {данные?.differs_from_portal?.length ? (
        <div className="card hint" style={{ marginBottom: 12 }}>
          <b>Чем этот рейтинг отличается от портального</b>
          <ul className="plain-list" style={{ marginTop: 6 }}>
            {данные.differs_from_portal.map((т) => <li key={т}>{т}</li>)}
          </ul>
        </div>
      ) : null}

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <ТаблицаРейтинга строки={данные?.rows ?? []} деньги={!!данные?.money}
                         период={данные?.period ?? период} refs={refs} />
        <Страницы всего={данные?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </>
  )
}

function ТаблицаРейтинга({ строки, деньги, период, refs }: {
  строки: РейтингСтрока[]; деньги: boolean; период: string; refs?: СправочникиP73G
}) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">За {месяцСловами(период)} рейтинг пуст</div>
        <div className="empty-hint">
          В рейтинг входят только не отключённые учётки с признаком инженера — у остальных
          баллы есть, а места нет.
        </div>
      </div>
    )
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th className="num">Место</th>
            <th>Инженер</th>
            <th className="num">Начислений за месяц</th>
            <th className="num">Верификаций</th>
            <th className="num">Бейджей</th>
            <th>Серия</th>
            {деньги && <th className="num">Баллы за месяц</th>}
            {деньги && <th className="num">Должны</th>}
          </tr>
        </thead>
        <tbody>
          {строки.map((r) => (
            <tr key={r.d1_id}>
              <td className="num" data-label="Место">{r.rank}</td>
              <td data-label="Инженер">
                <div>{r.full_name}</div>
                <div className="hint">
                  {поСправочнику(r.level, refs?.level_names, 'уровень не указан')}
                </div>
              </td>
              <td className="num" data-label="Начислений за месяц">{r.period_accruals}</td>
              <td className="num" data-label="Верификаций">{r.verifications}</td>
              <td className="num" data-label="Бейджей">{r.badges}</td>
              <td data-label="Серия">
                {r.current_streak ?? 0}
                <div className="hint">лучшая {r.longest_streak ?? 0}</div>
              </td>
              {деньги && (
                <td className="num" data-label="Баллы за месяц">{число(r.period_points ?? 0)}</td>
              )}
              {деньги && (
                <td className="num" data-label="Должны">{число(r.available_points ?? 0)}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ═══════════════════════════ вкладка «Выплаты» ═══════════════════════════

function Выплаты() {
  const [период, setПериод] = useState('')
  const [поиск, setПоиск] = useState('')
  const [start, setStart] = useState(0)

  const фильтры = useMemo(() => ({
    period: период || undefined,
    q: поиск || undefined,
    all: true,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [период, поиск, start])

  const запрос = useВыплаты(фильтры)
  const данные = запрос.data
  const итоги = данные?.totals
  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  return (
    <>
      <Закрыто почему={данные?.restricted_why} />

      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="p73g-payouts">
          <div className="f">
            <label htmlFor="epm">Месяц выплаты</label>
            <input id="epm" value={период} placeholder="ГГГГ-ММ"
                   onChange={(e) => сСброса(setПериод)(e.target.value)} />
          </div>
          <div className="f">
            <label htmlFor="epq">Поиск</label>
            <input id="epq" value={поиск} onChange={(e) => сСброса(setПоиск)(e.target.value)}
                   placeholder="фамилия или примечание" />
          </div>
        </FilterBar>
        {итоги && (
          <div className="hint" style={{ marginTop: 8 }}>
            {pluralN(итоги.payouts, 'выплата', 'выплаты', 'выплат')} на{' '}
            {fmtMoney(итоги.amount_rub)} ({число(итоги.points_paid)} баллов) —{' '}
            {итоги.people} получателей за {итоги.periods}{' '}
            {plural(итоги.periods, 'месяц', 'месяца', 'месяцев')}.
          </div>
        )}
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <ТаблицаВыплат строки={данные?.rows ?? []} />
        <Страницы всего={данные?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </>
  )
}

function ТаблицаВыплат({ строки }: { строки: ВыплатаСтрока[] }) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">Проведённых выплат под этот фильтр нет</div>
        <div className="empty-hint">
          Выплату проводит портал 73 после одобрения заявки; в зеркале она видна в течение
          нескольких минут.
        </div>
      </div>
    )
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Когда</th>
            <th>Кому</th>
            <th className="num">Сумма</th>
            <th className="num">Баллов списано</th>
            <th>Период</th>
            <th>Кто провёл</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((o) => (
            <tr key={o.d1_id}>
              <td data-label="Когда" className="nowrap">{fmtDate(o.created_at, true)}</td>
              <td data-label="Кому">{o.full_name}</td>
              <td className="num" data-label="Сумма">
                {fmtMoney(o.amount_rub)}
                {o.rub_minus_points !== 0 && (
                  <div className="error" style={{ fontSize: 12 }}>
                    рубли и баллы разошлись на {число(o.rub_minus_points)}
                  </div>
                )}
              </td>
              <td className="num" data-label="Баллов списано">{число(o.points_paid ?? 0)}</td>
              <td data-label="Период">{месяцСловами(o.period)}</td>
              <td data-label="Кто провёл">
                <div>{o.created_by_name}</div>
                {o.notes && <div className="hint">{o.notes}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ═══════════════════════════ вкладка «Заявки» ═══════════════════════════

function Заявки({ refs }: { refs?: СправочникиP73G }) {
  const [статус, setСтатус] = useState('')
  const [ждут, setЖдут] = useState(false)
  const [start, setStart] = useState(0)

  const фильтры = useMemo(() => ({
    status: статус || undefined,
    pending: ждут || undefined,
    all: true,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [статус, ждут, start])

  const запрос = useЗаявки(фильтры)
  const данные = запрос.data
  const итоги = данные?.totals
  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  return (
    <>
      <Закрыто почему={данные?.restricted_why} />

      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="p73g-requests">
          <div className="f">
            <label htmlFor="eqs">Состояние</label>
            <select id="eqs" value={статус}
                    onChange={(e) => сСброса(setСтатус)(e.target.value)}>
              <option value="">любое</option>
              {Object.entries(refs?.status_names ?? {}).map(([к, имя]) => (
                <option key={к} value={к}>{имя}</option>
              ))}
            </select>
          </div>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 18 }}>
            <input type="checkbox" checked={ждут}
                   onChange={(e) => сСброса(setЖдут)(e.target.checked)} />
            только ждут рассмотрения
          </label>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          {данные?.note ?? 'заявку отсюда нельзя ни подать, ни одобрить: и то и другое — '
            + 'запись в портал 73, а раздел только читает'}.
          {итоги && (
            <> Всего {pluralN(итоги.requests, 'заявка', 'заявки', 'заявок')} на{' '}
              {fmtMoney(итоги.amount_rub)}: одобрено {итоги.approved}, отклонено{' '}
              {итоги.rejected}, ждут {итоги.pending} на {fmtMoney(итоги.pending_rub)}.</>
          )}
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <ТаблицаЗаявок строки={данные?.rows ?? []} refs={refs} />
        <Страницы всего={данные?.total ?? 0} start={start} setStart={setStart} />
      </QueryState>
    </>
  )
}

function ТаблицаЗаявок({ строки, refs }: { строки: ЗаявкаСтрока[]; refs?: СправочникиP73G }) {
  if (!строки.length) {
    return (
      <div className="card empty">
        <div className="empty-title">Заявок под этот фильтр нет</div>
        <div className="empty-hint">
          Заявку инженер подаёт в портале 73 — из CRM это сделать нельзя, и пустой список
          здесь означает именно «заявок нет», а не «форма не открылась».
        </div>
      </div>
    )
  }
  return (
    <div className="table-wrap cards">
      <table>
        <thead>
          <tr>
            <th>Когда</th>
            <th>Кто</th>
            <th className="num">Баллов</th>
            <th className="num">Сумма</th>
            <th>Состояние</th>
            <th>Рассмотрение</th>
          </tr>
        </thead>
        <tbody>
          {строки.map((r) => (
            <tr key={r.d1_id}>
              <td data-label="Когда" className="nowrap">{fmtDate(r.created_at, true)}</td>
              <td data-label="Кто">
                <div>{r.full_name}</div>
                {r.notes && <div className="hint">{r.notes}</div>}
              </td>
              <td className="num" data-label="Баллов">{число(r.points)}</td>
              <td className="num" data-label="Сумма">{fmtMoney(r.amount_rub)}</td>
              <td data-label="Состояние">
                <span className={классСтатуса(r.status)}>
                  {поСправочнику(r.status, refs?.status_names, 'состояние не указано')}
                </span>
                {r.approved_without_payout && (
                  <div className="error" style={{ fontSize: 12 }}>
                    одобрена, а выплаты на эту сумму в реестре нет
                  </div>
                )}
              </td>
              <td data-label="Рассмотрение">
                {r.reviewed_at
                  ? <div className="nowrap">{fmtDate(r.reviewed_at, true)}</div>
                  : <span className="muted">ещё не рассмотрена</span>}
                <div className="hint">
                  {r.reviewed_at ? `${r.reviewed_by_name}, ` : ''}
                  {r.hours_to_review !== null && r.hours_to_review !== undefined
                    ? pluralN(r.hours_to_review, 'час', 'часа', 'часов')
                    : '—'}
                  {r.reviewed_at ? ' до решения' : ' в ожидании'}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ═══════════════════════════ вкладка «Сходимость» ═══════════════════════════
//
// Девять проверок, которых нет на портале. Портал верит колонке баланса; здесь она
// сверяется с журналом, реестром выплат и каталогом бейджей. Ни одна проверка ничего
// не чинит: править можно только в портале и только человеку.

function Сходимости({ запрос }: { запрос: ReturnType<typeof useСходимость> }) {
  const данные = запрос.data
  return (
    <>
      <Закрыто почему={данные?.restricted_why} />
      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {!данные?.rows.length ? (
          <div className="card empty">
            <div className="empty-title">Ядро не прислало ни одной проверки</div>
            <div className="empty-hint">
              Это не «всё сошлось»: список проверок приходит всегда, даже когда находок нет.
            </div>
          </div>
        ) : (
          <>
            <div className="card hint" style={{ marginBottom: 12 }}>
              {данные.checks_with_findings > 0
                ? <><b>Нашлось в {pluralN(данные.checks_with_findings, 'проверке',
                                          'проверках', 'проверках')} из {данные.total}.</b>{' '}</>
                : <><b>Все {данные.total} проверки чистые.</b>{' '}</>}
              {данные.note}
            </div>
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th>Проверка</th>
                    <th className="num">Строк</th>
                    <th>Примеры</th>
                  </tr>
                </thead>
                <tbody>
                  {данные.rows.map((п) => <СтрокаПроверки key={п.check} п={п} />)}
                </tbody>
              </table>
            </div>
          </>
        )}
      </QueryState>
    </>
  )
}

function СтрокаПроверки({ п }: { п: ПроверкаСтрока }) {
  return (
    <tr>
      <td data-label="Проверка">
        <div>{п.title}</div>
        <div className="hint">{п.why}</div>
      </td>
      <td className="num" data-label="Строк">
        {п.rows > 0 ? <span className="badge lose">{п.rows}</span> : 0}
      </td>
      <td data-label="Примеры">
        {п.rows === 0 ? (
          <span className="muted">расхождений нет</span>
        ) : (
          <ul className="plain-list">
            {п.samples.map((о, i) => (
              <li key={i}>{описаниеПримера(о)}</li>
            ))}
          </ul>
        )}
        {п.rows > п.samples.length && (
          <div className="hint">
            показаны первые {п.samples.length} из {п.rows}
          </div>
        )}
      </td>
    </tr>
  )
}

/**
 * Пример проверки — свободный набор полей: у каждой из девяти он свой. Печатаем «ключ:
 * значение» подряд, а не подгоняем разметку под каждую: девять форм в вёрстке разъедутся
 * с ядром при первой же новой проверке, а так новая проверка появится на экране сама.
 */
function описаниеПримера(о: Record<string, unknown>): string {
  return Object.entries(о)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([к, v]) => `${к}: ${typeof v === 'number' ? число(v) : String(v)}`)
    .join(' · ')
}
